import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  InputAdornment,
  Avatar,
  TablePagination,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  ManageAccounts as ManageIcon,
  FileDownload as ExportIcon,
  MenuBook as DirectoryIcon,
  MoreVert as MoreVertIcon,
  Email as InviteIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  FilterList as FilterListIcon,
  HowToReg as RegistrationIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import { fetchEmployees, deleteEmployee } from '../../store/slices/employeeSlice';
import { AppDispatch, RootState } from '../../store/store';
import { DropdownItem } from '../../types';
import { employeeApi } from '../../api/employeeApi';
import { getApiBaseUrl } from '../../api/axiosConfig';
import { masterApi } from '../../api/masterApi';
import { leaveApi } from '../../api/leaveApi';
import QuickAddEmployeeModal from './EmployeeForm';
import { LeaveTypeDto, LeavePeriodDto } from '../../types';

const ALL_ROWS_PAGE_SIZE = 100000;
const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];
type EmployeeSortKey = 'employee' | 'code' | 'contact' | 'designation' | 'department' | 'status';
type SortDirection = 'asc' | 'desc';
type ChangeFieldKey = 'departmentId' | 'designationId' | 'branchId';

const CHANGE_FIELD_LABELS: Record<ChangeFieldKey, string> = {
  departmentId: 'Department',
  designationId: 'Designation',
  branchId: 'Branch',
};

const CHANGE_FIELD_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 320,
    },
  },
};

export interface ExportSections {
  basicDetails: boolean;
  contactDetails: boolean;
  jobDetails: boolean;
  supervisorMapping: boolean;
  employeeSettings: boolean;
}

const EmployeeList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { employees, loading, totalCount } = useSelector((state: RootState) => state.employees);
  const user = useSelector((state: RootState) => state.auth.user);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [bulkAnchorEl, setBulkAnchorEl] = useState<null | HTMLElement>(null);
  const [bulkSelectionEnabled, setBulkSelectionEnabled] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [manageGroupOpen, setManageGroupOpen] = useState(false);
  const [manageGroupName, setManageGroupName] = useState('');
  const [manageGroupEmail, setManageGroupEmail] = useState('');
  const [bulkEntitlementOpen, setBulkEntitlementOpen] = useState(false);
  const [entitlementLeaveTypes, setEntitlementLeaveTypes] = useState<LeaveTypeDto[]>([]);
  const [entitlementLeavePeriods, setEntitlementLeavePeriods] = useState<LeavePeriodDto[]>([]);
  const [entitlementLeaveTypeId, setEntitlementLeaveTypeId] = useState('');
  const [entitlementLeavePeriodId, setEntitlementLeavePeriodId] = useState('');
  const [entitlementTotalDays, setEntitlementTotalDays] = useState<number>(0);
  const [entitlementCarryForwardDays, setEntitlementCarryForwardDays] = useState<number>(0);
  const [changeFieldOpen, setChangeFieldOpen] = useState(false);
  const [changeFieldKey, setChangeFieldKey] = useState<ChangeFieldKey>('departmentId');
  const [changeFieldValue, setChangeFieldValue] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const inviteLink = user?.tenantId
    ? `${window.location.origin}/employee-registration?tenantId=${encodeURIComponent(user.tenantId)}`
    : `${window.location.origin}/employee-registration`;
  const [exportSections, setExportSections] = useState<ExportSections>({
    basicDetails: true,
    contactDetails: false,
    jobDetails: false,
    supervisorMapping: false,
    employeeSettings: false,
  });

  // Filter state
  const [filterOpen, setFilterOpen] = useState(false);
  const [departments, setDepartments] = useState<DropdownItem[]>([]);
  const [designations, setDesignations] = useState<DropdownItem[]>([]);
  const [branches, setBranches] = useState<DropdownItem[]>([]);
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('');
  const [filterDesignationId, setFilterDesignationId] = useState<string>('');
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [filterEmploymentStatus, setFilterEmploymentStatus] = useState<string>('');
  const [filterJoinedDateFrom, setFilterJoinedDateFrom] = useState<string>('');
  const [filterJoinedDateTo, setFilterJoinedDateTo] = useState<string>('');
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [sortBy, setSortBy] = useState<EmployeeSortKey>('code');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    dispatch(fetchEmployees({
      page: page + 1,
      pageSize: rowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : rowsPerPage,
      searchTerm: activeSearchTerm,
      filters: activeFilters,
      sortBy,
      sortDirection,
    }));
  }, [dispatch, page, rowsPerPage, activeFilters, sortBy, sortDirection, activeSearchTerm]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [depts, desigs, brs] = await Promise.all([
          masterApi.getDepartments(),
          masterApi.getDesignations(),
          masterApi.getBranches(),
        ]);
        setDepartments(depts);
        setDesignations(desigs);
        setBranches(brs);
      } catch {
        // silently fail - dropdowns will be empty
      }
    };
    loadFilterOptions();
  }, []);

  useEffect(() => {
    // Keep selected IDs limited to employees currently available in the grid.
    setSelectedEmployeeIds((previous) => previous.filter((id) => employees.some((employee) => employee.id === id)));
  }, [employees]);

const handleSearch = () => {
  const searchValue = searchTerm.replace(/^\s+/, '').toLowerCase(); // remove only left spaces
  const normalizedSearchValue = searchValue || undefined;

  dispatch(
    fetchEmployees({
      searchTerm: normalizedSearchValue,
      page: 1,
      pageSize: rowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : rowsPerPage,
      filters: activeFilters,
      sortBy,
      sortDirection,
    })
  );

  setActiveSearchTerm(normalizedSearchValue);
  setPage(0);
};

  // const handleDelete = async (id: string) => {
  //   const result = await Swal.fire({
  //     title: 'Delete Employee',
  //     text: 'Are you sure you want to delete this employee?',
  //     icon: 'warning',
  //     showCancelButton: true,
  //     confirmButtonColor: '#d33',
  //     cancelButtonColor: '#6c757d',
  //     confirmButtonText: 'Yes, delete',
  //   });
  //   if (result.isConfirmed) {
  //     try {
  //       await dispatch(deleteEmployee(id)).unwrap();
  //       toast.success('Employee deleted successfully');
  //     } catch (error: any) {
  //       toast.error(error || 'Failed to delete employee');
  //     }
  //   }
  // };

  const handleDelete = (id: string) => {
  setDeleteTargetId(id);
  setDeleteConfirmText('');
  setDeleteConfirmOpen(true);
};

const handleConfirmDelete = async () => {
  if (!deleteTargetId || deleteConfirmText !== 'delete') return;
  try {
    await dispatch(deleteEmployee(deleteTargetId)).unwrap();
    toast.success('Employee deleted successfully');
  } catch (error: any) {
    toast.error(error || 'Failed to delete employee');
  } finally {
    setDeleteConfirmOpen(false);
    setDeleteTargetId(null);
    setDeleteConfirmText('');
  }
};

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (key: EmployeeSortKey) => {
    setSortBy((previousKey) => {
      if (previousKey === key) {
        setSortDirection((previousDirection) => (previousDirection === 'asc' ? 'desc' : 'asc'));
        return previousKey;
      }

      setSortDirection('asc');
      return key;
    });
    setPage(0);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUploadsBaseUrl = () =>
    getApiBaseUrl().replace(/\/api\/?$/, '');

  const getPhotoUrl = (filePath?: string | null) => {
    if (!filePath) return undefined;
    if (/^https?:\/\//i.test(filePath)) return filePath;
    return `${getUploadsBaseUrl()}/${filePath.replace(/^\/+/, '')}`;
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a', '#fee140'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const handleEmployeeCreated = (id: string) => {
    setQuickAddOpen(false);
    navigate(`/admin/employee/manage/${id}/basic`);
  };

  const handleToggleEmployeeSelection = (id: string) => {
    setSelectedEmployeeIds((previous) =>
      previous.includes(id) ? previous.filter((employeeId) => employeeId !== id) : [...previous, id]
    );
  };

  const handleToggleSelectAllVisible = (checked: boolean) => {
    const visibleEmployeeIds = employees.map((employee) => employee.id);

    if (checked) {
      setSelectedEmployeeIds((previous) => Array.from(new Set([...previous, ...visibleEmployeeIds])));
      return;
    }

    const visibleIdSet = new Set(visibleEmployeeIds);
    setSelectedEmployeeIds((previous) => previous.filter((id) => !visibleIdSet.has(id)));
  };

  const allVisibleEmployeesSelected =
    employees.length > 0 && employees.every((employee) => selectedEmployeeIds.includes(employee.id));
  const someVisibleEmployeesSelected = employees.some((employee) => selectedEmployeeIds.includes(employee.id));

  const refreshEmployeeList = async () => {
    await dispatch(
      fetchEmployees({
        searchTerm: activeSearchTerm,
        page: page + 1,
        pageSize: rowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : rowsPerPage,
        filters: activeFilters,
        sortBy,
        sortDirection,
      })
    );
  };

  const handleBulkStatusUpdate = async (employmentStatus: number, actionLabel: string) => {
    if (selectedEmployeeIds.length === 0) return;

    const selectedEmployees = employees.filter((employee) => selectedEmployeeIds.includes(employee.id));
    if (selectedEmployees.length === 0) {
      toast.error('No selected employees found on this page');
      return;
    }

    try {
      setBulkActionLoading(true);
      setBulkAnchorEl(null);

      await Promise.all(
        selectedEmployees.map((employee) =>
          employeeApi.updateBasicDetails(employee.id, {
            id: employee.id,
            employeeCode: employee.employeeCode,
            fullName: employee.fullName,
            firstName: employee.firstName || undefined,
            middleName: employee.middleName || undefined,
            lastName: employee.lastName || undefined,
            gender: employee.gender || undefined,
            bloodGroup: employee.bloodGroup || undefined,
            dateOfBirth: employee.dateOfBirth || undefined,
            maritalStatus: employee.maritalStatus || undefined,
            nationality: employee.nationality || undefined,
            religion: employee.religion || undefined,
            category: employee.category || undefined,
            aadhaarNumber: employee.aadhaarNumber || undefined,
            panNumber: employee.panNumber || undefined,
            passportNumber: employee.passportNumber || undefined,
            drivingLicense: employee.drivingLicense || undefined,
            uanNumber: employee.uanNumber || undefined,
            confirmationDate: employee.confirmationDate || undefined,
            employmentType: employee.employmentType || undefined,
            joinedDate: employee.joinedDate || undefined,
            organizationId: employee.organizationId || undefined,
            employmentStatus,
          })
        )
      );

      await refreshEmployeeList();
      setSelectedEmployeeIds([]);
      toast.success(`${selectedEmployees.length} employee(s) ${actionLabel} successfully`);
    } catch {
      toast.error(`Failed to ${actionLabel} selected employees`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEmployeeIds.length === 0) return;

    const result = await Swal.fire({
      title: 'Delete Selected Employees',
      text: `Are you sure you want to delete ${selectedEmployeeIds.length} employee(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) return;

    try {
      setBulkActionLoading(true);
      setBulkAnchorEl(null);

      await Promise.all(selectedEmployeeIds.map((id) => employeeApi.delete(id)));

      await refreshEmployeeList();
      setSelectedEmployeeIds([]);
      toast.success(`${selectedEmployeeIds.length} employee(s) deleted successfully`);
    } catch {
      toast.error('Failed to delete selected employees');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleOpenManageGroups = () => {
    if (selectedEmployeeIds.length === 0) return;
    setManageGroupName('');
    setManageGroupEmail('');
    setBulkAnchorEl(null);
    setManageGroupOpen(true);
  };

  const handleManageGroups = async () => {
    const groupName = manageGroupName.trim();
    if (!groupName) {
      toast.error('Group name is required');
      return;
    }

    try {
      setBulkActionLoading(true);
      await Promise.all(
        selectedEmployeeIds.map((id) =>
          employeeApi.addSubscriberGroup(id, {
            employeeId: id,
            groupName,
            subscriberEmail: manageGroupEmail.trim() || undefined,
            isActive: true,
          })
        )
      );
      setManageGroupOpen(false);
      setSelectedEmployeeIds([]);
      toast.success(`Group added for ${selectedEmployeeIds.length} employee(s)`);
    } catch {
      toast.error('Failed to manage groups for selected employees');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleOpenBulkEntitlements = async () => {
    if (selectedEmployeeIds.length === 0) return;

    try {
      setBulkActionLoading(true);
      const [types, periods] = await Promise.all([leaveApi.getLeaveTypes(), leaveApi.getLeavePeriods()]);
      setEntitlementLeaveTypes(types);
      setEntitlementLeavePeriods(periods);
      setEntitlementLeaveTypeId(types[0]?.id || '');
      setEntitlementLeavePeriodId(periods[0]?.id || '');
      setEntitlementTotalDays(0);
      setEntitlementCarryForwardDays(0);
      setBulkAnchorEl(null);
      setBulkEntitlementOpen(true);
    } catch {
      toast.error('Failed to load leave entitlements options');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkAddEntitlements = async () => {
    if (!entitlementLeaveTypeId || !entitlementLeavePeriodId) {
      toast.error('Leave type and leave period are required');
      return;
    }

    try {
      setBulkActionLoading(true);
      const result = await leaveApi.bulkCreateLeaveEntitlement({
        employeeIds: selectedEmployeeIds,
        leaveTypeId: entitlementLeaveTypeId,
        leavePeriodId: entitlementLeavePeriodId,
        totalDays: entitlementTotalDays,
        carryForwardDays: entitlementCarryForwardDays,
      });
      setBulkEntitlementOpen(false);
      setSelectedEmployeeIds([]);
      toast.success(`${result.count} entitlement(s) created`);
    } catch {
      toast.error('Failed to bulk add entitlements');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleOpenChangeField = () => {
    if (selectedEmployeeIds.length === 0) return;
    setChangeFieldKey('departmentId');
    setChangeFieldValue('');
    setBulkAnchorEl(null);
    setChangeFieldOpen(true);
  };

  const handleChangeEmployeeField = async () => {
    if (!changeFieldValue) {
      toast.error('Please select a value');
      return;
    }

    const selectedEmployees = employees.filter((employee) => selectedEmployeeIds.includes(employee.id));
    if (selectedEmployees.length === 0) {
      toast.error('No selected employees found on this page');
      return;
    }

    try {
      setBulkActionLoading(true);
      await Promise.all(
        selectedEmployees.map((employee) =>
          employeeApi.updateJobDetails(employee.id, {
            id: employee.id,
            departmentId: (changeFieldKey === 'departmentId' ? changeFieldValue : employee.departmentId) || undefined,
            designationId: (changeFieldKey === 'designationId' ? changeFieldValue : employee.designationId) || undefined,
            branchId: (changeFieldKey === 'branchId' ? changeFieldValue : employee.branchId) || undefined,
            shiftId: employee.shiftId || undefined,
            salaryStructureId: employee.salaryStructureId || undefined,
            workLocation: employee.workLocation || undefined,
            probationPeriod: employee.probationPeriod || undefined,
            joinedDate: employee.joinedDate || undefined,
            employmentStatus: employee.employmentStatus ?? 0,
          })
        )
      );

      await refreshEmployeeList();
      setChangeFieldOpen(false);
      setSelectedEmployeeIds([]);
      toast.success(`Field updated for ${selectedEmployees.length} employee(s)`);
    } catch {
      toast.error('Failed to change field for selected employees');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleTerminateEmployees = async () => {
    if (selectedEmployeeIds.length === 0) return;

    const result = await Swal.fire({
      title: 'Terminate Selected Employees',
      text: `Are you sure you want to terminate ${selectedEmployeeIds.length} employee(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, terminate',
    });

    if (!result.isConfirmed) return;
    await handleBulkStatusUpdate(2, 'terminated');
  };

  const renderSortableHeader = (key: EmployeeSortKey, label: string) => (
    <TableSortLabel
      active={sortBy === key}
      direction={sortBy === key ? sortDirection : 'asc'}
      onClick={() => handleSort(key)}
    >
      <strong>{label}</strong>
    </TableSortLabel>
  );

  const changeFieldLabel = CHANGE_FIELD_LABELS[changeFieldKey];
  const changeFieldOptions =
    changeFieldKey === 'departmentId' ? departments : changeFieldKey === 'designationId' ? designations : branches;
  const selectedChangeFieldOption = changeFieldOptions.find((item) => String(item.id) === changeFieldValue);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Employees
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<DirectoryIcon />}
            sx={{ borderRadius: 2 , textTransform: 'none'}}
            onClick={() => navigate('/employees/directory')}
          >
            Directory
          </Button>
          <Button
            variant="outlined"
            startIcon={<RegistrationIcon />}
            sx={{ borderRadius: 2 , textTransform: 'none'}}
            onClick={() => navigate('/employees/registration-requests')}

          >
            Registration Requests
          </Button>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={18} /> : <ExportIcon />}
            sx={{ borderRadius: 2 , textTransform: 'none'}}
            disabled={exporting}
            onClick={() => setExportModalOpen(true)}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<InviteIcon />}
            sx={{ borderRadius: 2 , textTransform: 'none'}}
            onClick={() => { setInviteModalOpen(true); setLinkCopied(false); }}
          >
            Invite Employees
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setQuickAddOpen(true)}
            sx={{ borderRadius: 2 , textTransform: 'none'}}
          >
            Add Employee
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1, sm: 0 } }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search employees..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            onKeyUp={(e) => {
              if (e.key !== 'Enter') handleSearch();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 350 } }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFilterOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
            color={Object.keys(activeFilters).length > 0 ? 'primary' : 'inherit'}
          >
            More Filters
            {Object.keys(activeFilters).length > 0 && (
              <Chip
                label={Object.keys(activeFilters).length}
                size="small"
                color="primary"
                sx={{ ml: 0.5, height: 20, minWidth: 20 }}
              />
            )}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
            <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            sx={{ borderRadius: 2 , textTransform: 'none'}}
            onClick={() => navigate('/employees/upLoadBulkData')}
          >
            Upload Bulk Data
          </Button> 
          <Button
            variant="outlined"
            disabled={bulkActionLoading}
            sx={{ borderRadius: 2 , textTransform: 'none',ml: { xs: 0, sm: 2 } }}
            onClick={(e) => {
              setBulkSelectionEnabled(true);
              setBulkAnchorEl(e.currentTarget);
            }}
            endIcon={<MoreVertIcon />}
          >
            Bulk Actions
          </Button>
          <Menu
            anchorEl={bulkAnchorEl}
            open={Boolean(bulkAnchorEl)}
            onClose={() => {
              if (!bulkActionLoading) {
                setBulkAnchorEl(null);
              }
            }}
          >
            <MenuItem
              disabled={selectedEmployeeIds.length === 0 || bulkActionLoading}
              onClick={() => handleBulkStatusUpdate(0, 'activated')}
            >
              Activate Selected ({selectedEmployeeIds.length})
            </MenuItem>
            <MenuItem
              disabled={selectedEmployeeIds.length === 0 || bulkActionLoading}
              onClick={() => handleBulkStatusUpdate(1, 'deactivated')}
            >
              Deactivate Selected ({selectedEmployeeIds.length})
            </MenuItem>
            <MenuItem
              disabled={selectedEmployeeIds.length === 0 || bulkActionLoading}
              onClick={handleBulkDelete}
            >
              Delete Selected ({selectedEmployeeIds.length})
            </MenuItem>
            <MenuItem
              disabled={selectedEmployeeIds.length === 0 || bulkActionLoading}
              onClick={handleOpenManageGroups}
            >
              Manage Groups ({selectedEmployeeIds.length})
            </MenuItem>
            <MenuItem
              disabled={selectedEmployeeIds.length === 0 || bulkActionLoading}
              onClick={handleOpenBulkEntitlements}
            >
              Bulk Add Entitlements ({selectedEmployeeIds.length})
            </MenuItem>
            <MenuItem
              disabled={selectedEmployeeIds.length === 0 || bulkActionLoading}
              onClick={handleOpenChangeField}
            >
              Change Employee Field ({selectedEmployeeIds.length})
            </MenuItem>
            <MenuItem
              disabled={selectedEmployeeIds.length === 0 || bulkActionLoading}
              onClick={handleTerminateEmployees}
            >
              Terminate Employees ({selectedEmployeeIds.length})
            </MenuItem>
            <MenuItem
              disabled={bulkActionLoading}
              onClick={() => {
                setSelectedEmployeeIds([]);
                setBulkSelectionEnabled(false);
                setBulkAnchorEl(null);
              }}
            >
              Close Bulk Selection
            </MenuItem>
          </Menu>
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Total Records: {totalCount}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {bulkSelectionEnabled && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allVisibleEmployeesSelected}
                        indeterminate={!allVisibleEmployeesSelected && someVisibleEmployeesSelected}
                        onChange={(e) => handleToggleSelectAllVisible(e.target.checked)}
                        inputProps={{ 'aria-label': 'Select all employees in current list' }}
                      />
                    </TableCell>
                  )}
                  <TableCell>{renderSortableHeader('employee', 'Employee')}</TableCell>
                  <TableCell>{renderSortableHeader('code', 'Code')}</TableCell>
                  <TableCell>{renderSortableHeader('contact', 'Contact')}</TableCell>
                  <TableCell>{renderSortableHeader('designation', 'Designation')}</TableCell>
                  <TableCell>{renderSortableHeader('department', 'Department')}</TableCell>
                  <TableCell>{renderSortableHeader('status', 'Status')}</TableCell>
                  <TableCell align="center"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={bulkSelectionEnabled ? 8 : 7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No employees found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((employee) => (
                    <TableRow key={employee.id} hover sx={{ cursor: 'pointer' }}>
                      {bulkSelectionEnabled && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedEmployeeIds.includes(employee.id)}
                            onChange={() => handleToggleEmployeeSelection(employee.id)}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ 'aria-label': `Select ${employee.fullName}` }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={getPhotoUrl(employee.profileImagePath)}
                            sx={{ bgcolor: getAvatarColor(employee.fullName), width: 36, height: 36, fontSize: 14 }}
                          >
                            {!employee.profileImagePath && getInitials(employee.fullName)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500}>
                            {employee.fullName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={employee.employeeCode} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{employee.mobileNumber || '-'}</TableCell>
                      <TableCell>{employee.designationName || '-'}</TableCell>
                      <TableCell>{employee.departmentName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={employee.employmentStatusName || 'Unknown'}
                          color={(() => {
                            switch ((employee.employmentStatusName || '').toLowerCase()) {
                              case 'working': return 'success';
                              case 'inactive': return 'default';
                              case 'resigned': return 'warning';
                              case 'terminated': return 'error';
                              default: return 'default';
                            }
                          })()}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Manage">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/admin/employee/manage/${employee.id}/basic`)}
                          >
                            <ManageIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(employee.id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            />
          </>
        )}
      </TableContainer>

      {/* More Filters Dialog */}
      <Dialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          More Filters
          <IconButton size="small" onClick={() => setFilterOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Department</InputLabel>
                <Select
                  native
                  value={filterDepartmentId}
                  onChange={(e) => setFilterDepartmentId(e.target.value as string)}
                  label="Department"
                  notched
                >
                  <option value="">All</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Designation</InputLabel>
                <Select
                  native
                  value={filterDesignationId}
                  onChange={(e) => setFilterDesignationId(e.target.value as string)}
                  label="Designation"
                  notched
                >
                  <option value="">All</option>
                  {designations.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Branch</InputLabel>
                <Select
                  native
                  value={filterBranchId}
                  onChange={(e) => setFilterBranchId(e.target.value as string)}
                  label="Branch"
                  notched
                >
                  <option value="">All</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Employment Status</InputLabel>
                <Select
                  native
                  value={filterEmploymentStatus}
                  onChange={(e) => setFilterEmploymentStatus(e.target.value as string)}
                  label="Employment Status"
                  notched
                >
                  <option value="">All</option>
                  <option value="0">Working</option>
                  <option value="1">Inactive</option>
                  <option value="2">Resigned</option>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <TextField
                label="Joined Date From"
                type="date"
                size="small"
                fullWidth
                value={filterJoinedDateFrom}
                onChange={(e) => setFilterJoinedDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <TextField
                label="Joined Date To"
                type="date"
                size="small"
                fullWidth
                value={filterJoinedDateTo}
                onChange={(e) => setFilterJoinedDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Button
            onClick={() => {
              setFilterDepartmentId('');
              setFilterDesignationId('');
              setFilterBranchId('');
              setFilterEmploymentStatus('');
              setFilterJoinedDateFrom('');
              setFilterJoinedDateTo('');
              setActiveFilters({});
              setFilterOpen(false);
              setPage(0);
            }}
            color="inherit"
          >
            Clear Filters
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const filters: any = {};
              if (filterDepartmentId) filters.departmentId = filterDepartmentId;
              if (filterDesignationId) filters.designationId = filterDesignationId;
              if (filterBranchId) filters.branchId = filterBranchId;
              if (filterEmploymentStatus !== '') filters.employmentStatus = Number(filterEmploymentStatus);
              if (filterJoinedDateFrom) filters.joinedDateFrom = filterJoinedDateFrom;
              if (filterJoinedDateTo) filters.joinedDateTo = filterJoinedDateTo;
              setActiveFilters(filters);
              setFilterOpen(false);
              setPage(0);
            }}
          >
            View Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Options Dialog */}

      <Dialog
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Choose Export Options
          <IconButton size="small" onClick={() => setExportModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {([
            { key: 'basicDetails' as keyof ExportSections, label: 'Basic Details' },
            { key: 'contactDetails' as keyof ExportSections, label: 'Contact Details' },
            { key: 'jobDetails' as keyof ExportSections, label: 'Job Details' },
            { key: 'supervisorMapping' as keyof ExportSections, label: 'Supervisor Mapping' },
            { key: 'employeeSettings' as keyof ExportSections, label: 'Employee Settings' },
          ]).map((section) => (
            <FormControlLabel
              key={section.key}
              control={
                <Checkbox
                  checked={exportSections[section.key]}
                  onChange={(e) =>
                    setExportSections((prev) => ({ ...prev, [section.key]: e.target.checked }))
                  }
                />
              }
              label={section.label}
              sx={{ display: 'block', ml: 0 }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <ExportIcon />}
            disabled={exporting || !Object.values(exportSections).some(Boolean)}
            onClick={async () => {
              try {
                setExporting(true);
                await employeeApi.exportEmployees(searchTerm || undefined, exportSections);
                setExportModalOpen(false);
              } catch (err) {
                toast.error('Failed to export employees');
              } finally {
                setExporting(false);
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={inviteModalOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setInviteModalOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{ 'data-app-dialog-skip-top-close': 'true' }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Typography variant="h6" fontWeight="bold">Invite Employee</Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            1. Copy and share this link with your employees.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 0.5,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'grey.50',
              cursor: 'pointer',
            }}
            onClick={() => {
              navigator.clipboard.writeText(inviteLink);
              setLinkCopied(true);
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontFamily: 'monospace',
                letterSpacing: 0.5,
                wordBreak: 'break-all',
                flex: 1,
              }}
            >
              {inviteLink}
            </Typography>
            <Tooltip title={linkCopied ? 'Copied!' : 'Copy link'}>
              <IconButton size="small" sx={{ ml: 1 }}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>

          {linkCopied && (
            <Typography variant="caption" color="success.main" sx={{ mb: 1.5, display: 'block' }}>
              Link copied to clipboard!
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tip: If you have a WhatsApp Group for your company, you can share it there.
          </Typography>

          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            2. Registration by Employees
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
            <li><Typography variant="body2">Employees can verify their mobile number via OTP</Typography></li>
            <li><Typography variant="body2">Complete the registration form</Typography></li>
          </Box>

          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            3. Review &amp; Approve
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
            <li>
              <Typography variant="body2">
                Go to <strong>Administration &amp; Reporting &gt; Employee Registration Requests</strong> in your HRMS Portal
              </Typography>
            </li>
            <li><Typography variant="body2">Approve or reject the registrations</Typography></li>
            <li>
              <Typography variant="body2">
                Once approved, employees can log in via web and mobile app
              </Typography>
            </li>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setInviteModalOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => setInviteModalOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manageGroupOpen}
        onClose={() => !bulkActionLoading && setManageGroupOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage Groups</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Group Name"
            value={manageGroupName}
            onChange={(e) => setManageGroupName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Subscriber Email (Optional)"
            value={manageGroupEmail}
            onChange={(e) => setManageGroupEmail(e.target.value)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageGroupOpen(false)} disabled={bulkActionLoading} color="inherit" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleManageGroups} disabled={bulkActionLoading} sx={{ textTransform: 'none' }}>
            {bulkActionLoading ? 'Saving...' : `Apply to ${selectedEmployeeIds.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkEntitlementOpen}
        onClose={() => !bulkActionLoading && setBulkEntitlementOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Add Entitlements</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Leave Type</InputLabel>
            <Select
              native
              value={entitlementLeaveTypeId}
              onChange={(e) => setEntitlementLeaveTypeId(e.target.value as string)}
              label="Leave Type"
            >
              <option value="">Select Leave Type</option>
              {entitlementLeaveTypes.map((leaveType) => (
                <option key={leaveType.id} value={leaveType.id}>{leaveType.name}</option>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Leave Period</InputLabel>
            <Select
              native
              value={entitlementLeavePeriodId}
              onChange={(e) => setEntitlementLeavePeriodId(e.target.value as string)}
              label="Leave Period"
            >
              <option value="">Select Leave Period</option>
              {entitlementLeavePeriods.map((leavePeriod) => (
                <option key={leavePeriod.id} value={leavePeriod.id}>{leavePeriod.name}</option>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Total Days"
            type="number"
            value={entitlementTotalDays}
            onChange={(e) => setEntitlementTotalDays(Number(e.target.value) || 0)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />

          <TextField
            label="Carry Forward Days"
            type="number"
            value={entitlementCarryForwardDays}
            onChange={(e) => setEntitlementCarryForwardDays(Number(e.target.value) || 0)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEntitlementOpen(false)} disabled={bulkActionLoading} color="inherit" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleBulkAddEntitlements} disabled={bulkActionLoading} sx={{ textTransform: 'none' }}>
            {bulkActionLoading ? 'Applying...' : `Apply to ${selectedEmployeeIds.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={changeFieldOpen}
        onClose={() => !bulkActionLoading && setChangeFieldOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Employee Field</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="change-employee-field-label">Field</InputLabel>
            <Select
              labelId="change-employee-field-label"
              value={changeFieldKey}
              onChange={(e) => {
                setChangeFieldKey(e.target.value as ChangeFieldKey);
                setChangeFieldValue('');
              }}
              label="Field"
              MenuProps={CHANGE_FIELD_MENU_PROPS}
            >
              <MenuItem value="departmentId">Department</MenuItem>
              <MenuItem value="designationId">Designation</MenuItem>
              <MenuItem value="branchId">Branch</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={changeFieldOptions.length === 0}>
            <InputLabel id="change-employee-value-label" shrink>
              Value
            </InputLabel>
            <Select
              labelId="change-employee-value-label"
              value={changeFieldValue}
              onChange={(e) => setChangeFieldValue(String(e.target.value))}
              label="Value"
              displayEmpty
              notched
              renderValue={(selected) => {
                const selectedValue = String(selected);
                if (!selectedValue) {
                  return (
                    <Typography component="span" color="text.secondary">
                      Select {changeFieldLabel}
                    </Typography>
                  );
                }

                return selectedChangeFieldOption?.name || selectedValue;
              }}
              MenuProps={CHANGE_FIELD_MENU_PROPS}
              sx={{
                '& .MuiSelect-select': {
                  alignItems: 'center',
                  display: 'flex',
                  minHeight: 24,
                  whiteSpace: 'normal',
                },
              }}
            >
              <MenuItem value="" disabled>
                Select {changeFieldLabel}
              </MenuItem>
              {changeFieldOptions.map((item) => (
                <MenuItem key={String(item.id)} value={String(item.id)} sx={{ whiteSpace: 'normal' }}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {changeFieldOptions.length === 0
                ? `No ${changeFieldLabel.toLowerCase()} values available`
                : `Choose the ${changeFieldLabel.toLowerCase()} to apply to selected employees`}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeFieldOpen(false)} disabled={bulkActionLoading} color="inherit" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleChangeEmployeeField} disabled={bulkActionLoading} sx={{ textTransform: 'none' }}>
            {bulkActionLoading ? 'Updating...' : `Apply to ${selectedEmployeeIds.length}`}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
          open={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setDeleteTargetId(null);
            setDeleteConfirmText('');
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold" color="error">
              Delete Employee
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeleteTargetId(null);
                setDeleteConfirmText('');
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This action <strong>cannot be undone</strong>. Please type{' '}
              <strong style={{ color: '#d33' }}>delete</strong> to confirm.
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder='Type "delete" to confirm'
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && deleteConfirmText === 'delete') {
                  handleConfirmDelete();
                }
              }}
              error={deleteConfirmText.length > 0 && deleteConfirmText !== 'delete'}
              helperText={
                deleteConfirmText.length > 0 && deleteConfirmText !== 'delete'
                  ? 'Text does not match'
                  : ' '
              }
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeleteTargetId(null);
                setDeleteConfirmText('');
              }}
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={deleteConfirmText !== 'delete'}
              onClick={handleConfirmDelete}
              startIcon={<DeleteIcon />}
              sx={{ textTransform: 'none' }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

      <QuickAddEmployeeModal
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        onCreated={handleEmployeeCreated}
      />
    </Box>
  );
};

export default EmployeeList;
